import {
  PlanPhase,
  PlanGeneratorInput,
  PlanGeneratorConfig,
  PlanGenerationResult,
  WeekPlan,
  PlanSession,
  SessionCustomizations,
  PlanSummary,
  TrainingPlan,
  AthleteSettings,
  ZoneType,
  SessionDiscipline,
} from '../../types/athlete';
import { getPhaseStructure, getPhaseForWeek, getPhaseFocus } from './periodization';
import { selectSessionsForWeek, getSessionTemplates } from './sessionSelector';
import { validateSessionPlacement, validateWeekPlan } from './placementValidator';
import { getRunWalkBreakdown, RunWalkBreakdown } from './runWalkCalculator';
import { calculateSessionTargets, formatZoneTarget } from './zoneCalculator';

export const generateTrainingPlan = async (
  input: PlanGeneratorInput,
  athleteSettings: AthleteSettings
): Promise<PlanGenerationResult> => {
  if (input.idealWeekTemplate && input.sessionTemplates) {
    return generatePlanFromIdealWeek(input, athleteSettings);
  }

  const warnings: string[] = [];
  const planConfig = getPhaseStructure(input.raceType, input.sessionsPerWeek);

  const weeks: WeekPlan[] = [];
  const allSessions: PlanSession[] = [];

  const startDate = new Date(input.raceDate);
  const totalWeeks = planConfig.totalWeeks;

  for (let weekNum = totalWeeks; weekNum >= 1; weekNum--) {
    const weekStartDate = new Date(startDate);
    weekStartDate.setDate(weekStartDate.getDate() - (weekNum - 1) * 7);
    const weekEndDate = new Date(weekStartDate);
    weekEndDate.setDate(weekEndDate.getDate() + 6);

    const phase = getPhaseForWeek(weekNum, input.raceType, input.sessionsPerWeek);
    const phaseFocus = getPhaseFocus(phase);

    const weekSessions = generateWeekSessions(
      weekNum,
      input,
      phase,
      athleteSettings,
      weekStartDate.toISOString().split('T')[0],
      planConfig
    );

    const totalSwim = weekSessions
      .filter(s => s.discipline === 'swim')
      .reduce((sum, s) => sum + s.planned_duration_minutes, 0);
    const totalBike = weekSessions
      .filter(s => s.discipline === 'bike')
      .reduce((sum, s) => sum + s.planned_duration_minutes, 0);
    const totalRun = weekSessions
      .filter(s => s.discipline === 'run')
      .reduce((sum, s) => sum + s.planned_duration_minutes, 0);
    const totalStrength = weekSessions.filter(s => s.discipline === 'strength').length;

    const weekValidation = validateWeekPlan(weekSessions);
    if (weekValidation.warnings.length > 0) {
      warnings.push(
        `Week ${weekNum} (${phase}): ${weekValidation.warnings.map((w) => w.message).join('; ')}`
      );
    }

    weeks.push({
      weekNumber: weekNum,
      phase,
      focus: phaseFocus,
      startDate: weekStartDate.toISOString().split('T')[0],
      endDate: weekEndDate.toISOString().split('T')[0],
      sessions: weekSessions,
      totalSwimMinutes: totalSwim,
      totalBikeMinutes: totalBike,
      totalRunMinutes: totalRun,
      totalStrengthSessions: totalStrength,
      warnings: weekValidation.warnings.map(w => w.message),
    });

    allSessions.push(...weekSessions);
  }

  const plan: TrainingPlan = {
    id: `plan-${Date.now()}`,
    user_id: '',
    name: `${input.raceType.charAt(0).toUpperCase() + input.raceType.slice(1)} Triathlon Plan`,
    description: `${input.raceType} training plan for ${input.experienceLevel} athletes`,
    start_date: weeks[weeks.length - 1]?.startDate || input.raceDate,
    end_date: input.raceDate,
    status: 'draft',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const phaseBreakdown: { phase: string; weeks: number }[] = [];
  weeks.forEach(week => {
    const existing = phaseBreakdown.find(p => p.phase === week.phase);
    if (existing) {
      existing.weeks++;
    } else {
      phaseBreakdown.push({ phase: week.phase, weeks: 1 });
    }
  });

  const weeklyTimeCommitment = {
    average: Math.round(
      allSessions.reduce((sum, s) => sum + s.planned_duration_minutes, 0) / totalWeeks
    ),
    peak: Math.max(...weeks.map(w => w.sessions.reduce((sum, s) => sum + s.planned_duration_minutes, 0))),
    taper: Math.round(
      weeks
        .filter(w => w.phase === 'Taper')
        .flatMap(w => w.sessions)
        .reduce((sum, s) => sum + s.planned_duration_minutes, 0) /
        (weeks.filter(w => w.phase === 'Taper').length || 1)
    ),
  };

  const summary: PlanSummary = {
    raceType: input.raceType,
    raceDate: input.raceDate,
    weeksToRace: totalWeeks,
    sessionsPerWeek: input.sessionsPerWeek,
    experienceLevel: input.experienceLevel,
    totalWeeks,
    phaseBreakdown,
    weeklyTimeCommitment,
  };

  return {
    plan,
    sessions: allSessions,
    warnings,
    summary,
  };
};

const generateWeekSessions = (
  weekNumber: number,
  input: PlanGeneratorInput,
  phase: PlanPhase,
  athleteSettings: AthleteSettings,
  weekStartDate: string,
  planConfig: PlanGeneratorConfig
): PlanSession[] => {
  const sessions = selectSessionsForWeek({
    raceType: input.raceType,
    experienceLevel: input.experienceLevel,
    phase,
    sessionsPerWeek: input.sessionsPerWeek,
    preferredDays: input.preferredDays,
    weekdayMaxDuration: input.maxWeekdayDuration,
    weekendMaxDuration: input.maxWeekendDuration,
  });

  const sessionElements: PlanSession[] = [];
  const availableDays = [...input.preferredDays].sort((a, b) => a - b);

  let sessionIndex = 0;
  for (const sessionInfo of sessions) {
    if (sessionIndex >= availableDays.length) break;

    const dayOfWeek = availableDays[sessionIndex];
    const scheduledDate = getScheduledDate(weekStartDate, dayOfWeek);
    const template = getTemplateForSessionInfo(sessionInfo, input);

    if (!template) {
      sessionIndex++;
      continue;
    }

    const zoneType = template.zone_type as ZoneType;
    const zoneValue = template.zone_value;

    const targets = calculateSessionTargets(
      sessionInfo.discipline,
      zoneType,
      zoneValue,
      athleteSettings
    );

    let runWalkBreakdown: RunWalkBreakdown | null = null;
    if (sessionInfo.discipline === 'run' && template.has_run_walk) {
      const runPaceValue = formatZoneTarget('run', 'T', 0, athleteSettings);
      const thresholdPace: { name: string; perKm: string; perMile: string; description: string } = {
        name: 'Threshold',
        perKm: runPaceValue || '6:00',
        perMile: runPaceValue
          ? `${Math.round(parseInt(runPaceValue.split(':')[0]) * 1.6)}:${runPaceValue.split(':')[1]}`
          : '9:39',
        description: 'Threshold pace',
      };

      runWalkBreakdown = getRunWalkBreakdown(
        sessionInfo.duration,
        thresholdPace,
        true,
        template.run_walk_ratio
      );
    }

    const customizations: SessionCustomizations = {
      duration: sessionInfo.duration,
      zoneType,
      notes: template.description || undefined,
    };

    const sessionWalkTargets = runWalkBreakdown
      ? {
          enabled: true as const,
          ratio: runWalkBreakdown.ratio,
          runPace: runWalkBreakdown.runPace,
          description: runWalkBreakdown.description,
          breakdown: (mins: number) => runWalkBreakdown!.formatBreakdown(mins),
        }
      : undefined;

    const session: PlanSession = {
      id: `session-${weekNumber}-${sessionIndex}-${Date.now()}`,
      plan_id: '',
      week_number: weekNumber,
      day_of_week: dayOfWeek,
      scheduled_date: scheduledDate,
      session_template_id: template.id,
      session_name: template.name,
      discipline: sessionInfo.discipline,
      category: template.category,
      planned_duration_minutes: sessionInfo.duration,
      target_zone_type: zoneType,
      target_zone_value: zoneValue,
      has_run_walk: template.has_run_walk,
      run_walk_ratio: template.has_run_walk ? template.run_walk_ratio : null,
      run_walk_description: runWalkBreakdown?.description || null,
      is_brick: template.is_brick,
      is_long_session: sessionInfo.isLong,
      brick_run_zone: template.is_brick ? template.brick_run_zone : null,
      brick_run_duration: template.is_brick ? template.brick_run_duration : null,
      customizations,
      validation_warnings: [],
      targets: {
        ...targets,
        runWalk: sessionWalkTargets,
      },
    };

    sessionElements.push(session);
    sessionIndex++;
  }

  return sessionElements;
};

const generatePlanFromIdealWeek = async (
  input: PlanGeneratorInput,
  athleteSettings: AthleteSettings
): Promise<PlanGenerationResult> => {
  const warnings: string[] = [];
  const planConfig = getPhaseStructure(input.raceType, input.sessionsPerWeek || 6);
  const totalWeeks = planConfig.totalWeeks;
  const startDate = new Date(input.raceDate);
  const weeks: WeekPlan[] = [];
  const allSessions: PlanSession[] = [];

  const templateDays = input.idealWeekTemplate?.days || {};
  const templates = input.sessionTemplates || [];

  const planId = `plan-${Date.now()}`;

  for (let weekNum = totalWeeks; weekNum >= 1; weekNum--) {
    const weekStartDate = new Date(startDate);
    weekStartDate.setDate(weekStartDate.getDate() - (weekNum - 1) * 7);
    const weekEndDate = new Date(weekStartDate);
    weekEndDate.setDate(weekEndDate.getDate() + 6);

    const weekScale = getWeekScale(weekNum);
    const intensityShift = getWeekIntensityShift(weekNum);

    const weekSessions: PlanSession[] = [];

    Object.entries(templateDays).forEach(([dayKey, daySessions]) => {
      const dayOfWeek = Number(dayKey);
      daySessions
        .slice()
        .sort((a, b) => a.orderTime.localeCompare(b.orderTime))
        .forEach((idealSession, sessionIndex) => {
          const template = templates.find(t => t.id === idealSession.templateId);
          if (!template) return;

          const scheduledDate = getScheduledDate(weekStartDate.toISOString().split('T')[0], dayOfWeek);
          const defaultDuration = Math.round((template.min_duration + template.max_duration) / 2);
          const baseDuration = idealSession.durationOverride ?? defaultDuration;
          const scaledDuration = Math.max(15, Math.round(baseDuration * weekScale));

          const zoneType = idealSession.intensityOverride?.zoneType || template.zone_type;
          const zoneValue = idealSession.intensityOverride?.zoneValue ?? template.zone_value;
          const scaledZone = scaleZoneType(zoneType, intensityShift);

          const targets = calculateSessionTargets(
            template.discipline,
            scaledZone,
            zoneValue,
            athleteSettings
          );

          const runWalkBreakdown = template.discipline === 'run' && template.has_run_walk
            ? getRunWalkBreakdown(
                scaledDuration,
                {
                  name: 'Threshold',
                  perKm: formatZoneTarget('run', 'T', 0, athleteSettings) || '6:00',
                  perMile: formatZoneTarget('run', 'T', 0, athleteSettings)
                    ? `${Math.round(parseInt((formatZoneTarget('run', 'T', 0, athleteSettings) || '6:00').split(':')[0]) * 1.6)}:${(formatZoneTarget('run', 'T', 0, athleteSettings) || '6:00').split(':')[1]}`
                    : '9:39',
                  description: 'Threshold pace',
                },
                true,
                template.run_walk_ratio
              )
            : null;

          const sessionWalkTargets = runWalkBreakdown
            ? {
                enabled: true as const,
                ratio: runWalkBreakdown.ratio,
                runPace: runWalkBreakdown.runPace,
                description: runWalkBreakdown.description,
                breakdown: (mins: number) => runWalkBreakdown.formatBreakdown(mins),
              }
            : undefined;

          const session: PlanSession = {
            id: `session-${weekNum}-${dayOfWeek}-${sessionIndex}-${Date.now()}`,
            plan_id: planId,
            week_number: weekNum,
            day_of_week: dayOfWeek,
            scheduled_date: scheduledDate,
            order_time: idealSession.orderTime,
            session_template_id: template.id,
            session_name: template.name,
            discipline: template.discipline,
            category: template.category,
            planned_duration_minutes: scaledDuration,
            target_zone_type: scaledZone,
            target_zone_value: zoneValue,
            has_run_walk: template.has_run_walk,
            run_walk_ratio: template.has_run_walk ? template.run_walk_ratio : null,
            run_walk_description: runWalkBreakdown?.description || null,
            is_brick: template.is_brick,
            is_long_session: template.is_long_session,
            brick_run_zone: template.is_brick ? template.brick_run_zone : null,
            brick_run_duration: template.is_brick ? template.brick_run_duration : null,
            customizations: {
              duration: scaledDuration,
              zoneType: scaledZone,
              notes: template.description || undefined,
            },
            validation_warnings: [],
            targets: {
              ...targets,
              runWalk: sessionWalkTargets,
            },
          };

          weekSessions.push(session);
        });
    });

    const totalSwim = weekSessions
      .filter(s => s.discipline === 'swim')
      .reduce((sum, s) => sum + s.planned_duration_minutes, 0);
    const totalBike = weekSessions
      .filter(s => s.discipline === 'bike')
      .reduce((sum, s) => sum + s.planned_duration_minutes, 0);
    const totalRun = weekSessions
      .filter(s => s.discipline === 'run')
      .reduce((sum, s) => sum + s.planned_duration_minutes, 0);
    const totalStrength = weekSessions.filter(s => s.discipline === 'strength').length;

    const weekValidation = validateWeekPlan(weekSessions);
    if (weekValidation.warnings.length > 0) {
      warnings.push(
        `Week ${weekNum} (Build Cycle): ${weekValidation.warnings.map(w => w.message).join('; ')}`
      );
    }

    weeks.push({
      weekNumber: weekNum,
      phase: 'Build Cycle',
      focus: weekScale === 0.6 ? 'Recovery' : 'Build',
      startDate: weekStartDate.toISOString().split('T')[0],
      endDate: weekEndDate.toISOString().split('T')[0],
      sessions: weekSessions,
      totalSwimMinutes: totalSwim,
      totalBikeMinutes: totalBike,
      totalRunMinutes: totalRun,
      totalStrengthSessions: totalStrength,
      warnings: weekValidation.warnings.map(w => w.message),
    });

    allSessions.push(...weekSessions);
  }

  const plan: TrainingPlan = {
    id: planId,
    user_id: '',
    name: `${input.raceType.charAt(0).toUpperCase() + input.raceType.slice(1)} Triathlon Plan`,
    description: `${input.raceType} training plan for ${input.experienceLevel} athletes`,
    start_date: weeks[weeks.length - 1]?.startDate || input.raceDate,
    end_date: input.raceDate,
    status: 'draft',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const weeklyTimeCommitment = {
    average: Math.round(
      allSessions.reduce((sum, s) => sum + s.planned_duration_minutes, 0) / totalWeeks
    ),
    peak: Math.max(...weeks.map(w => w.sessions.reduce((sum, s) => sum + s.planned_duration_minutes, 0))),
    taper: 0,
  };

  const summary: PlanSummary = {
    raceType: input.raceType,
    raceDate: input.raceDate,
    weeksToRace: totalWeeks,
    sessionsPerWeek: weeks[0]?.sessions.length || 0,
    experienceLevel: input.experienceLevel,
    totalWeeks,
    phaseBreakdown: [{ phase: 'Build Cycle', weeks: totalWeeks }],
    weeklyTimeCommitment,
  };

  return {
    plan,
    sessions: allSessions,
    warnings,
    summary,
  };
};

const getWeekScale = (weekNumber: number): number => {
  const cycleWeek = ((weekNumber - 1) % 4) + 1;
  if (cycleWeek === 1) return 1;
  if (cycleWeek === 2) return 1.08;
  if (cycleWeek === 3) return 1.16;
  return 0.6;
};

const getWeekIntensityShift = (weekNumber: number): number => {
  const cycleWeek = ((weekNumber - 1) % 4) + 1;
  if (cycleWeek === 1) return 0;
  if (cycleWeek === 2) return 1;
  if (cycleWeek === 3) return 1;
  return -1;
};

const scaleZoneType = (zoneType: ZoneType, shift: number): ZoneType => {
  const orderedZones: ZoneType[] = ['none', 'E', 'L', 'M', 'T', 'I', 'Z1', 'Z2', 'Z3', 'Z4', 'Z5', 'Z6', 'Z7', 'CSS', 'CSS+X', 'CSS-X'];
  const zoneIndex = orderedZones.indexOf(zoneType);
  if (zoneIndex === -1 || shift === 0) return zoneType;
  const shiftedIndex = Math.min(orderedZones.length - 1, Math.max(0, zoneIndex + shift));
  return orderedZones[shiftedIndex];
};

const getScheduledDate = (weekStartDate: string, dayOfWeek: number): string => {
  const date = new Date(weekStartDate);
  const dayOffset = dayOfWeek - 1;
  date.setDate(date.getDate() + dayOffset);
  return date.toISOString().split('T')[0];
};

const getTemplateForSessionInfo = (
  sessionInfo: { discipline: SessionDiscipline; category: string; isLong: boolean; isBrick: boolean },
  input: PlanGeneratorInput
) => {
  const templates = getSessionTemplates().filter(t => {
    if (t.race_types && !t.race_types.includes(input.raceType)) return false;
    if (t.experience_levels && !t.experience_levels.includes(input.experienceLevel)) return false;
    if (t.discipline !== sessionInfo.discipline) return false;
    const desiredCategory = sessionInfo.isBrick ? 'brick' : (sessionInfo.category as any);
    if (t.category !== desiredCategory) return false;
    if (t.is_long_session !== sessionInfo.isLong) return false;
    if (t.is_brick !== sessionInfo.isBrick) return false;
    return true;
  });

  return templates[0] || null;
};

export const regenerateWeekSessions = async (
  weekNumber: number,
  phase: PlanPhase,
  input: PlanGeneratorInput,
  athleteSettings: AthleteSettings,
  existingSessions: PlanSession[]
): Promise<{ sessions: PlanSession[]; warnings: string[] }> => {
  const warnings: string[] = [];

  const newSessions = generateWeekSessions(
    weekNumber,
    input,
    phase,
    athleteSettings,
    existingSessions[0]?.scheduled_date?.split('T')[0] || input.raceDate,
    getPhaseStructure(input.raceType, input.sessionsPerWeek)
  );

  const weekValidation = validateWeekPlan(newSessions);
  if (!weekValidation.valid) {
    warnings.push(...weekValidation.warnings.map(w => w.message));
  }

  const otherWeekSessions = existingSessions.filter(s => s.week_number !== weekNumber);
  const allWeekSessions = [...otherWeekSessions, ...newSessions];

  for (const session of newSessions) {
    const placementWarnings = validateSessionPlacement(session, session.day_of_week, allWeekSessions);
    session.validation_warnings = placementWarnings.map((w) => w.message);
  }

  return { sessions: newSessions, warnings };
};

export const moveSessionToDay = (
  session: PlanSession,
  newDay: number,
  allSessions: PlanSession[]
): { session: PlanSession; warnings: string[] } => {
  const warnings: string[] = [];

  const updatedSession = {
    ...session,
    day_of_week: newDay,
    scheduled_date: getScheduledDateFromWeek(
      session.scheduled_date,
      session.day_of_week,
      newDay
    ),
  };

  const otherSessions = allSessions.filter(s => s.id !== session.id);
  const placementWarnings = validateSessionPlacement(updatedSession, newDay, [
    ...otherSessions,
    updatedSession,
  ]);

  warnings.push(...placementWarnings.map((w) => w.message));
  updatedSession.validation_warnings = placementWarnings.map((w) => w.message);

  return { session: updatedSession, warnings };
};

const getScheduledDateFromWeek = (
  currentDate: string,
  currentDay: number,
  newDay: number
): string => {
  const date = new Date(currentDate);
  const dayOffset = newDay - currentDay;
  date.setDate(date.getDate() + dayOffset);
  return date.toISOString().split('T')[0];
};

export const getPlanOverview = (
  weeks: WeekPlan[]
): {
  totalWeeks: number;
  totalSessions: number;
  totalTimeMinutes: number;
  avgSessionsPerWeek: number;
  peakWeek: { weekNumber: number; minutes: number } | null;
  phaseDistribution: { phase: string; count: number }[];
} => {
  const allSessions = weeks.flatMap(w => w.sessions);
  const sessionCounts = weeks.map(w => w.sessions.length);
  const timePerWeek = weeks.map(w =>
    w.sessions.reduce((sum, s) => sum + s.planned_duration_minutes, 0)
  );

  const phaseCount: Record<string, number> = {};
  weeks.forEach(w => {
    phaseCount[w.phase] = (phaseCount[w.phase] || 0) + 1;
  });

  const peakWeekIndex = timePerWeek.indexOf(Math.max(...timePerWeek));

  return {
    totalWeeks: weeks.length,
    totalSessions: allSessions.length,
    totalTimeMinutes: allSessions.reduce((sum, s) => sum + s.planned_duration_minutes, 0),
    avgSessionsPerWeek: sessionCounts.reduce((a, b) => a + b, 0) / weeks.length,
    peakWeek:
      peakWeekIndex >= 0
        ? { weekNumber: weeks[peakWeekIndex].weekNumber, minutes: timePerWeek[peakWeekIndex] }
        : null,
    phaseDistribution: Object.entries(phaseCount).map(([phase, count]) => ({ phase, count })),
  };
};
